import React, { useEffect, useState, useContext } from "react";
import { FaComment, FaHeart, FaTag, FaTrash } from "react-icons/fa";
import { useParams } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../../context/user-context";
import {
  Container,
  Spinner,
  Row,
  Col,
  Card,
  Form,
  Button,
} from "react-bootstrap";
import EventSidebarLinks from "../../components/events/sidebar-links";
import { Breadcrumb } from "react-bootstrap";
import useBreadcrumbs from "use-react-router-breadcrumbs";

export default function EventGallery() {
  const { event_id } = useParams();
  const [gallery, setGallery] = useState([]);
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(null);
  const { userToken, user, userTags, setUserTags } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState([]);
  const breadcrumbs = useBreadcrumbs();

  useEffect(() => {
    getGallery();
  }, [event_id]);

  useEffect(() => {
    if (searchTerm) {
      getMembers(searchTerm);
    }
  }, [searchTerm]);

  const getGallery = async () => {
    if (!event_id) return;
    setLoading(true);
    try {
      const time = new Date().getTime();
      const eventResponse = await axios.get(process.env.REACT_APP_API_BASE_URL + `/wp-json/gnetwork/v1/events/${event_id}?timestamp=${time}`, {
        headers: {
          'Authorization': 'Basic ' + btoa(user.user_login + ":" + userToken),
          'Content-Type': 'application/json',
        }
      });
      setEvent(eventResponse.data.data);
      const response = await axios.get(
        process.env.REACT_APP_API_BASE_URL +
          `/wp-json/gnetwork/v1/events/${event_id}/gallery?timestamp=${time}`,
        {
          headers: {
            Authorization: "Basic " + btoa(user.user_login + ":" + userToken),
            "Content-Type": "application/json",
          },
        }
      );
      // console.log("Gallery API Response:", response); // <-- :white_check_mark: Add this line
      if (response?.data?.gallery) {
        const updatedGallery = response.data.gallery.map((image) => {
          const savedTag = userTags[image.media_id]; // Use media_id instead of id
          if (savedTag) {
            return { ...image, taggedMember: savedTag };
          }
          return image;
        });
        setGallery(updatedGallery);
      }
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMembers = async (search) => {
    if (!search) return;
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_BASE_URL +
          `/wp-json/gnetwork/v1/members?search=${search}`,
        {
          headers: {
            Authorization: "Basic " + btoa(user.user_login + ":" + userToken),
            "Content-Type": "application/json",
          },
        }
      );
      if (response?.data?.members) {
        setMembers(response.data.members);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTagClick = (mediaId) => {
    setShowSearch(mediaId);
    setSearchTerm("");
    setMembers([]);
  };

  const handleTagMember = async (member, mediaId) => {
    if (!mediaId) {
      console.error("Media ID is missing or undefined!");
      return;
    }
    const position = "top: 32%; left: 43%;";
    try {
      const updatedGallery = gallery.map((img) => {
        if (img.media_id === mediaId) {
          const existingTags = img.tags || [];
          return {
            image: mediaId,
            tags: [...existingTags, { position, user_id: member.id }],
          };
        }
        return { image: img.media_id, tags: img.tags || [] };
      });
      const response = await axios.put(
        process.env.REACT_APP_API_BASE_URL + `/wp-json/gnetwork/v1/events/${event_id}/gallery`,
        { gallery: updatedGallery },
        {
          headers: {
            Authorization: "Basic " + btoa(user.user_login + ":" + userToken),
            "Content-Type": "application/json",
          },
        }
      );
      // console.log("PUT tagging response:", response);
      // Update context and UI
      setUserTags((prevTags) => ({
        ...prevTags,
        [mediaId]: member,
      }));
      setGallery((prevGallery) =>
        prevGallery.map((image) => {
          if (image.media_id === mediaId) {
            return {
              ...image,
              tags: [...(image.tags || []), { user: member, position }],
            };
          }
          return image;
        })
      );
      setShowSearch(null);
    } catch (error) {
      console.error("Error tagging member via PUT:", error);
    }
  };

  return (
    <Container fluid className="p-relative">
      <EventSidebarLinks event={event} />
      <Container className="event-stats-container py-3">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        {
          <Breadcrumb>
            {breadcrumbs.map(({ breadcrumb, key, location, match }, index) => {
              breadcrumb =
                key === "/events/" + event_id ? event?.post_title : breadcrumb;
              return (
                <Breadcrumb.Item key={index} href={key}>
                  {breadcrumb}
                </Breadcrumb.Item>
              );
            })}
          </Breadcrumb>
        }
        {!loading && event && (
          <>
            <h2 className="mb-4">Event Gallery</h2>
            <Row xs={1} sm={2} md={3} lg={4} className="g-4">
              {gallery.map((item, index) => (
                <Col key={index}>
                  <Card className="h-100 position-relative border-0 shadow-sm">
                    <Card.Img
                      variant="top"
                      src={item.image.url}
                      alt={item.image.title || `Gallery ${index + 1}`}
                      style={{
                        objectFit: "cover",
                        height: "100%",
                        width: "100%",
                      }}
                    />
                    {/* Tag Icon */}
                    <div
                      className="position-absolute top-0 end-0 p-2"
                      style={{ cursor: "pointer", zIndex: 10 }}
                      onClick={() => handleTagClick(item.media_id)}
                    >
                      <FaTag size={20} color="white" />
                    </div>
                    {item.tags &&
                      item.tags.length > 0 &&
                      item.tags.map((tag, tagIndex) => (
                        <div
                          key={tagIndex}
                          className="position-absolute"
                          style={{
                            top: tag.position.split(";")[0],
                            left: tag.position.split(";")[1],
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            color: "white",
                            padding: "5px",
                            borderRadius: "3px",
                            zIndex: 10,
                          }}
                        >
                          <strong>
                            {tag.user.user_firstname} {tag.user.user_lastname}
                          </strong>
                        </div>
                      ))}
                    {/* Search box */}
                    {showSearch === item.media_id && (
                      <div className="position-absolute top-0 start-0 p-3 w-100">
                        <Form.Control
                          type="text"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          placeholder="Search members"
                        />
                        {members.length > 0 && (
                          <div className="bg-white shadow-sm mt-2 rounded">
                            {members.map((member) => (
                              <Button
                                key={member.id}
                                variant="light"
                                className="w-100 text-start"
                                onClick={() =>
                                  handleTagMember(member, item.media_id)
                                }
                              >
                                {member.name}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <Card.Footer className="d-flex justify-content-between position-absolute w-full bottom-0" style={{ columnGap: '0.5rem' }}>
                      <Button size="sm">
                        <FaHeart />
                      </Button>
                      <Button size="sm">
                        <FaComment />
                      </Button>
                      <Button size="sm">
                        <FaTrash />
                      </Button>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
      </Container>
    </Container>
  );
}
